import React, { useState, useEffect } from 'react';
import MDBox from 'components/MDBox';
import Grid from '@mui/material/Grid';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { GridWrapper } from 'components/Ui/styled';
import DeltaDataGrid from 'utils/CommonDataGrid/CommonDataGrid';
import BookedSessionStatusDialog from './BookedSessionStatusDialog';
import { FormInputDate } from 'components/Common/Forms/FormInputDate';
import FormInputDropdown from 'components/Common/Forms/FormInputDropDown';
import { booleanOptions, intialPageSize, dateFormatYMD } from 'constants';
import { getSearchedStudents } from 'layouts/students/service/studentsApi';
import { FormInputAutoCompleteSearch } from 'components/Common/Forms/FormAutoCompleteSearch';
import { setBookedSessionPagination } from 'layouts/tutoring/store/bookedSessionPaginationSlice/bookedSessionPaginationSlice';
import { resetBookedSessionPagination } from 'layouts/tutoring/store/bookedSessionPaginationSlice/bookedSessionPaginationSlice';
import { setBookedSessionPaginationPreviousKey } from 'layouts/tutoring/store/bookedSessionPaginationSlice/bookedSessionPaginationSlice';
import {
  getBookedSessions,
  getSession,
} from 'layouts/tutoring/services/tutoringApi';
import {
  getOptionLabel,
  getButtonSpinner,
  useNotification,
} from 'utils/commonUtils';
import { useDispatch } from 'react-redux';
import {
  setDataGridCounts,
  resetDataGridCounts,
} from 'utils/commonSlice/dataGridCounts/dataGridCountsSlice';
import {
  setSubject,
  setEarliestDate,
  setLatestDate,
  setAscending,
  setIncludeCancelled,
  resetBookedSessionFilter,
} from 'layouts/tutoring/store/bookedSessionFilterSlice/bookedSessionFilterSlice';
import {
  bookedSessionColumns,
  getBookedSessionListData,
} from './BookedSessionsDataGrid';
import { format } from 'date-fns';
import BookedSessionCancelDialog from './BookedSessionCancelDialog';
import BookedSessionInfoDialog from './BookedSessionInfoDialog';

const BookedSessions = () => {
  const dispatch = useDispatch();
  const { NotificationPopup, handleErrorResponse } = useNotification();

  const [studentOptions, setStudentOptions] = useState([]);
  const [studentSearchQuery, setStudentSearchQuery] = useState('');
  const [student, setStudent] = useState(null);
  const [isStudentSearchloading, setIsStudentSearchloading] = useState(false);
  const [bookedSessionPageKey, setBookedSessionPageKey] = useState('');
  const [bookedSessionList, setBookedSessionList] = useState({
    isLoading: false,
    rows: [],
    pageSize: intialPageSize,
    page: 1,
    total: 3000,
    currentPaginationKey: '',
    nextPaginationKey: '',
    count: '',
  });
  const [isFormLoading, setIsFormLoading] = useState(false);
  const [statusForm, setStatusForm] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [bookedSessionInfo, setBookedSessionInfo] = useState({});
  const [bookedSessionStatusDialog, setBookedSessionStatusDialog] =
    useState(false);
  const [bookedSessionViewDialog, setBookedSessionViewDialog] = useState(false);
  const [bookedSessionCancelDialog, setBookedSessionCancelDialog] =
    useState(false);

  const { pageSize, page, nextPaginationKey, count } = bookedSessionList;

  const bookedSessionFilters = useSelector(
    (state) => state.bookedSessionFilters
  );

  const {
    subject,
    subjectIds,
    earliestDate,
    latestDate,
    ascending,
    includeCancelled,
  } = bookedSessionFilters;

  const earliestDateYMD = earliestDate
    ? format(new Date(earliestDate), dateFormatYMD)
    : '';
  const latestDateYMD = latestDate
    ? format(new Date(latestDate), dateFormatYMD)
    : '';

  const bookedSessionPagination = useSelector(
    (state) => state?.bookedSessionPaginationKeyList?.bookedSessionPagination
  );

  const dataGridCounts = useSelector(
    (state) => state?.totalDataGridCounts?.dataGridCounts
  );
  const totalNumber = dataGridCounts?.reduce((acc, curr) => acc + curr, 0);

  const subjects = useSelector((state) => state?.subjects);

  const subjectOptions = subjects?.map((item) => ({
    id: item.subjectId,
    value: item.name,
  }));

  useEffect(() => {
    if (page === 1) {
      dispatch(resetBookedSessionPagination());
      dispatch(resetDataGridCounts());
      dispatch(setDataGridCounts(pageSize));
    }
  }, []);

  const defaultValue = {
    studentId: student?.studentId,
    subject: subject,
    earliestDate: earliestDate,
    latestDate: latestDate,
    ascending: ascending,
    includeCancelled: includeCancelled,
  };

  const useFunction = useForm({
    mode: 'onChange',
    defaultValues: defaultValue,
  });

  const { reset, control, register, setValue } = useFunction;

  useEffect(() => {
    if (!bookedSessionList?.isLoading) {
      if (count < pageSize) {
        setBookedSessionList((prveState) => ({
          ...prveState,
          total: totalNumber,
        }));
      }
    }
  }, [count, pageSize]);

  useEffect(() => {
    reset(defaultValue);
  }, [
    defaultValue.studentId,
    defaultValue.subject,
    defaultValue.earliestDate,
    defaultValue.latestDate,
    defaultValue.ascending,
    defaultValue.includeCancelled,
  ]);

  useEffect(() => {
    fetchBookedSessions();
  }, [
    pageSize,
    bookedSessionPageKey,
    student,
    subject,
    earliestDate,
    latestDate,
    ascending,
    includeCancelled,
  ]);

  const fetchBookedSessions = async () => {
    setBookedSessionList({ ...bookedSessionList, isLoading: true });
    try {
      const bookedSessionListData = await getBookedSessions(
        pageSize,
        bookedSessionPageKey,
        earliestDateYMD,
        latestDateYMD,
        student?.studentId,
        subject,
        ascending,
        includeCancelled
      );
      setBookedSessionList({ ...bookedSessionList, isLoading: false });
      setBookedSessionList({
        ...bookedSessionList,
        currentPaginationKey: bookedSessionListData?.currentPaginationKey,
        nextPaginationKey: bookedSessionListData?.nextPaginationKey,
        count: bookedSessionListData?.count,
      });
      setBookedSessionList((prev) => ({
        ...prev,
        rows: getBookedSessionListData(bookedSessionListData),
      }));
    } catch (error) {
      handleErrorResponse(error?.response?.data?.message);
      setBookedSessionList({ ...bookedSessionList, isLoading: false });
    }
  };

  useEffect(() => {
    fetchSearchedStudents();
  }, [studentSearchQuery]);

  const fetchSearchedStudents = async () => {
    try {
      if (studentSearchQuery?.length >= 1) {
        setIsStudentSearchloading(true);
        const searchedStudents = await getSearchedStudents(
          studentSearchQuery,
          true,
          false,
          20
        );
        setStudentOptions(searchedStudents?.items);
        setIsStudentSearchloading(false);
      }
    } catch (error) {
      setIsStudentSearchloading(false);
      handleErrorResponse(error?.response?.data?.message);
    }
  };

  const redirect = () => {
    dispatch(resetBookedSessionPagination());
    dispatch(resetDataGridCounts());
    dispatch(setDataGridCounts(pageSize));
    setBookedSessionList((prveState) => ({
      ...prveState,
      page: 1,
      total: 3000,
    }));
    setBookedSessionPageKey('');
  };

  const handleSubject = (e) => {
    dispatch(setSubject(e.target.value));
    redirect();
  };

  const handleEarliestDate = (e) => {
    dispatch(setEarliestDate(e.target.value));
    redirect();
  };

  const handleLatestDate = (e) => {
    dispatch(setLatestDate(e.target.value));
    redirect();
  };

  const handleAscending = (e) => {
    dispatch(setAscending(e.target.value));
    redirect();
  };

  const handleIncludeCancelled = (e) => {
    dispatch(setIncludeCancelled(e.target.value));
    redirect();
  };

  const handelPageChange = (page) => {
    setBookedSessionList((prveState) => ({
      ...prveState,
      page: page + 1,
    }));
    const paginationIdValue = {
      id: page,
      value: nextPaginationKey,
    };
    if (bookedSessionPagination?.find((item) => item.id === page)) {
      const getPreviousPaginationKey = () => {
        const getPaginationKey = bookedSessionPagination?.find(
          (item) => item.id === page
        );
        return getPaginationKey?.value;
      };
      const previousPaginationKey = getPreviousPaginationKey();
      dispatch(setBookedSessionPaginationPreviousKey(previousPaginationKey));
      setBookedSessionPageKey(previousPaginationKey);
    } else {
      dispatch(setDataGridCounts(count));
      dispatch(setBookedSessionPagination(paginationIdValue));
      setBookedSessionPageKey(nextPaginationKey);
    }
    if (page < 1) {
      setBookedSessionList((prveState) => ({
        ...prveState,
        total: 3000,
      }));
      dispatch(resetBookedSessionPagination());
      dispatch(resetDataGridCounts());
      dispatch(setDataGridCounts(pageSize));
      setBookedSessionPageKey('');
    }
  };

  const handleOnPageSizeChange = (newPageSize) => {
    if (count < pageSize) {
      setBookedSessionList((prveState) => ({
        ...prveState,
        pageSize: newPageSize,
        page: 1,
        total: totalNumber,
      }));
    } else {
      setBookedSessionList((prveState) => ({
        ...prveState,
        pageSize: newPageSize,
        page: 1,
        total: 3000,
      }));
      dispatch(resetBookedSessionPagination());
      dispatch(resetDataGridCounts());
      dispatch(setDataGridCounts(newPageSize));
    }
  };

  const handleStatus = async (params) => {
    const { row } = params;
    try {
      setIsFormLoading(true);
      const sessionData = await getSession(row.sessionId);
      setStatusForm(sessionData?.session);
      setIsFormLoading(false);
      setBookedSessionStatusDialog(true);
    } catch (error) {
      setIsFormLoading(false);
      setBookedSessionStatusDialog(false);
      handleErrorResponse(error?.response?.data?.message);
    }
  };

  const handleCancel = (params) => {
    const { row } = params;
    setSessionId(row.sessionId);
    setBookedSessionCancelDialog(true);
  };

  const handleViewBookedInfo = async (params) => {
    const { row } = params;
    try {
      setIsFormLoading(true);
      const sessionData = await getSession(row.sessionId);
      setBookedSessionInfo(sessionData?.session);
      setIsFormLoading(false);
      setBookedSessionViewDialog(true);
    } catch (error) {
      setIsFormLoading(false);
      setBookedSessionViewDialog(false);
      handleErrorResponse(error?.response?.data?.message);
    }
  };

  const handleClearFilters = () => {
    dispatch(resetBookedSessionFilter());
  };

  return (
    <>
      <MDBox mt={1}></MDBox>
      <GridWrapper container spacing={2}>
        <Grid item xs={12} sm={6}>
          <FormInputAutoCompleteSearch
            label="Search for student by name or ID"
            placeholder="Enter name or ID..."
            options={studentOptions}
            setSelectedInput={setStudent}
            setInputSearchQuery={setStudentSearchQuery}
            isSearchloading={isStudentSearchloading}
            getOptionLabel={getOptionLabel}
            value={student}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormInputDropdown
            name="subject"
            type="text"
            control={control}
            options={subjectOptions}
            defaultValue={defaultValue?.subject}
            label="Subject"
            register={register('subject', {
              onChange: (e) => {
                handleSubject(e);
              },
            })}
          />
        </Grid>
      </GridWrapper>
      <GridWrapper container spacing={2}>
        <Grid item xs={12} sm={6} md={2.4}>
          <FormInputDate
            name="earliestDate"
            control={control}
            label="Earliest Date"
            maxDate={defaultValue.latestDate}
            value={earliestDate}
            register={register('earliestDate', {
              onChange: (e) => {
                handleEarliestDate(e);
              },
            })}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <FormInputDate
            name="latestDate"
            control={control}
            label="Latest Date"
            minDate={defaultValue.earliestDate}
            value={latestDate}
            register={register('latestDate', {
              onChange: (e) => {
                handleLatestDate(e);
              },
            })}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <FormInputDropdown
            name="ascending"
            type="text"
            control={control}
            options={booleanOptions}
            defaultValue={defaultValue?.ascending}
            label="Ascending"
            register={register('ascending', {
              onChange: (e) => {
                handleAscending(e);
              },
            })}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <FormInputDropdown
            name="includeCancelled"
            type="text"
            control={control}
            options={booleanOptions}
            defaultValue={defaultValue?.includeCancelled}
            label="Include Cancelled"
            register={register('includeCancelled', {
              onChange: (e) => {
                handleIncludeCancelled(e);
              },
            })}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          {getButtonSpinner(false, handleClearFilters, 'Clear Filters')}
        </Grid>
      </GridWrapper>
      <MDBox mt={2}></MDBox>
      <DeltaDataGrid
        listData={bookedSessionList}
        columns={bookedSessionColumns(
          handleStatus,
          handleCancel,
          handleViewBookedInfo
        )}
        handelPageChange={handelPageChange}
        setListData={handleOnPageSizeChange}
      />
      <BookedSessionStatusDialog
        bookedSessionStatusDialog={bookedSessionStatusDialog}
        setBookedSessionStatusDialog={setBookedSessionStatusDialog}
        isFormLoading={isFormLoading}
        statusForm={statusForm}
        fetchBookedSessions={fetchBookedSessions}
      />
      <BookedSessionCancelDialog
        bookedSessionCancelDialog={bookedSessionCancelDialog}
        setBookedSessionCancelDialog={setBookedSessionCancelDialog}
        sessionId={sessionId}
        fetchBookedSessions={fetchBookedSessions}
      />
      <BookedSessionInfoDialog
        bookedSessionViewDialog={bookedSessionViewDialog}
        setBookedSessionViewDialog={setBookedSessionViewDialog}
        bookedSessionInfo={bookedSessionInfo}
      />
      <NotificationPopup />
    </>
  );
};

export default BookedSessions;
